import './ToggleButtonGroup.css';

import { ToggleButton, ToggleButtonProps } from 'primereact/togglebutton';
import { IconType } from 'primereact/utils';
import { FieldValues, useController, UseControllerProps } from 'react-hook-form';

import ErrorMessage from '../../error-message';

export type ToggleButtonGroupProps<T extends FieldValues> = {
  name: string;
  buttonGroupDetails: {
    label: string;
    onIcon: IconType<ToggleButtonProps>;
    offIcon: IconType<ToggleButtonProps>;
    value: string;
  }[];
} & UseControllerProps<T>;

const ToggleButtonGroup = <T extends FieldValues>({
  name,
  control,
  buttonGroupDetails,
}: ToggleButtonGroupProps<T>) => {
  const {
    field: { onChange, ref, ...inputProps },
    fieldState: { invalid, error },
  } = useController({
    name,
    control,
  });

  const handleOnChange = (index: number) => {
    onChange(buttonGroupDetails[index].value);
  };

  return (
    <div>
      <div className='flex gap-3 flex-wrap'>
        {buttonGroupDetails?.map(
          (
            buttonDetail: {
              label: string;
              value: string;
              onIcon: IconType<ToggleButtonProps>;
              offIcon: IconType<ToggleButtonProps>;
            },
            index: number,
          ) => {
            return (
              <div key={index} className='flex-1'>
                <ToggleButton
                  pt={{
                    input: {
                      style: {
                        appearance: 'none',
                      },
                    },
                  }}
                  onLabel={buttonDetail.label}
                  offLabel={buttonDetail.label}
                  onIcon={buttonDetail.onIcon}
                  className={`pacific-toggle-button ${
                    inputProps.value === buttonDetail.value ? 'p-checked' : ''
                  }`}
                  offIcon={buttonDetail.offIcon}
                  checked={inputProps.value === buttonDetail.value}
                  onClick={() => {
                    handleOnChange(index);
                  }}
                />
              </div>
            );
          },
        )}
      </div>

      {/* Hidden input to enable focus & auto-scroll on error */}
      <input readOnly className='opacity-0 p-0 m-0 w-0 h-0 absolute' ref={ref} />

      {invalid && (
        <div className='py-2'>
          <ErrorMessage message={error?.message || ''} />
        </div>
      )}
    </div>
  );
};

export default ToggleButtonGroup;
